import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';
import { Alignment } from '@app/core/enums/questionnaire.enums';

export interface ButtonOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-button-select-options',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './button-select-options.html',
  styleUrl: './button-select-options.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonSelectOptions),
      multi: true,
    },
  ],
})
export class ButtonSelectOptions implements ControlValueAccessor, OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() allowMultiple: boolean = false;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;
  
  // ✅ Updated output format to match checkbox/radio-button component
  @Output() answerChange = new EventEmitter<{ 
    questionId: string; 
    options: { optionId: string; optionSelected: string }[]; 
  }>();

  alignment = Alignment;
  getAlignmentClass(question: any): string {
    return question.questionSpecificOptions?.alignment === this.alignment.HORIZONTAL
      ? 'alignment-horizontal'
      : 'alignment-vertical';
  }

  options: ButtonOption[] = [];
  selectedValues: string[] = [];
  disabled = false;
  othersTextMap: Map<string, string> = new Map(); // Store text for each Others option
  private isUpdating = false; // Flag to prevent writeValue from overwriting during toggle
  private othersOptionIds: Set<string> = new Set(); // Cache all Others option IDs

  onChange = (value: string | string[]) => {};
  onTouched = () => {};

  ngOnInit() {
    // Cache all Others option IDs for faster lookups
    this.othersOptionIds = this.findOthersOptionIds();
    
    this.options = this.question?.questionOptions?.map(opt => ({
      value: opt.optionId,
      label: opt.text
    })) || [];

    if (this.question?.questionSpecificOptions?.allowMultipleResponses !== undefined) {
      this.allowMultiple = Boolean(this.question.questionSpecificOptions.allowMultipleResponses);
    }

    // Only set initial values once, don't subscribe continuously.
    if (this.formControl?.value) {
      if (this.allowMultiple) {
        this.selectedValues = Array.isArray(this.formControl.value) ? [...this.formControl.value] : [];
      } else {
        const value = Array.isArray(this.formControl.value) ? this.formControl.value[0] : this.formControl.value;
        this.selectedValues = value ? [value] : [];
      }
    }
  }

  writeValue(value: string | string[]): void {
    // Don't overwrite if we're currently updating internally
    if (this.isUpdating) {
      return;
    }
    
    const previousValues = new Set(this.selectedValues);
    
    if (this.allowMultiple) {
      this.selectedValues = value && Array.isArray(value) ? [...value] : [];
    } else {
      const optionId = Array.isArray(value) ? value[0] : value;
      this.selectedValues = optionId && typeof optionId === 'string' ? [optionId] : [];
    }

    // Clear others text for options that are no longer selected
    const currentValues = new Set(this.selectedValues);
    previousValues.forEach(optionId => {
      if (!currentValues.has(optionId) && this.othersOptionIds.has(optionId)) {
        this.othersTextMap.delete(optionId);
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onButtonClick(option: ButtonOption) {
    if (this.disabled) return;

    // Set flag to prevent writeValue from overwriting during update
    this.isUpdating = true;

    const index = this.selectedValues.indexOf(option.value);

    if (this.allowMultiple) {
      if (index > -1) {
        // Uncheck: remove from selected values
        this.selectedValues.splice(index, 1);
        // Clear others text if it was an Others option
        if (this.othersOptionIds.has(option.value)) {
          this.othersTextMap.delete(option.value);
        }
      } else {
        // Check: add to selected values
        this.selectedValues = [...this.selectedValues, option.value];
      }
    } else {
      const previousValue = this.selectedValues[0];
      this.selectedValues = [option.value];
      // Clear others text if switching away from Others option
      if (previousValue && previousValue !== option.value && this.othersOptionIds.has(previousValue)) {
        this.othersTextMap.delete(previousValue);
      }
    }

    this.updateValue();

    // Reset flag after a short delay to allow any async operations to complete
    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  updateValue() {
    const value = this.allowMultiple ? this.selectedValues : this.selectedValues[0] || '';
    
    // ✅ Update form control without re-triggering valueChanges
    if (this.formControl) {
      this.formControl.setValue(value, { emitEvent: false });
      this.formControl.markAsTouched();
    }

    // ✅ Notify Angular forms - this might trigger writeValue, but our flag will prevent overwrite
    this.onChange(value);
    this.onTouched();

    // ✅ Emit payload with ALL currently selected options (matching checkbox/radio format)
    this.emitAnswer();
  }

  private emitAnswer() {
    if (!this.question?.questionId) return;

    // Map ALL selected option IDs to the required format with optionSelected
    const formattedOptions = this.selectedValues.map(optionId => ({
      optionId,
      optionSelected: this.othersOptionIds.has(optionId) ? (this.othersTextMap.get(optionId) || '') : ''
    }));

    this.answerChange.emit({
      questionId: this.question.questionId,
      options: formattedOptions,
    });

    console.log('emitAnswer', {
      questionId: this.question.questionId,
      options: formattedOptions,
    });
  }

  isSelected(option: ButtonOption): boolean {
    return this.selectedValues.includes(option.value);
  }

  isOthersOption(option: ButtonOption): boolean {
    if (!this.question?.questionOptions) return false;
    const originalOption = this.question.questionOptions.find(opt => opt.optionId === option.value);
    return originalOption?.text?.toLowerCase().trim() === 'others';
  }

  isOthersSelected(optionId: string): boolean {
    return this.othersOptionIds.has(optionId) && this.selectedValues.includes(optionId);
  }

  private findOthersOptionIds(): Set<string> {
    const ids = new Set<string>();
    if (!this.question?.questionOptions) return ids;
    this.question.questionOptions.forEach(opt => {
      if (opt.text?.toLowerCase().trim() === 'others') {
        ids.add(opt.optionId);
      }
    });
    return ids;
  }

  onOthersTextChange(optionId: string, text: string) {
    this.othersTextMap.set(optionId, text);
    // Only emit answer, don't update form control to prevent re-render issues
    this.emitAnswer();
  }

  onOthersInputBlur() {
    // Ensure answer is emitted when user leaves the field
    this.emitAnswer();
    this.onTouched();
  }

  getOthersText(optionId: string): string {
    return this.othersTextMap.get(optionId) || '';
  }
}
